import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Row, Col } from 'reactstrap';

import apiFiles from 'api/files';
import apiOffers from 'api/offers';
import IOfferProductImage from 'api/interfaces/offers/IOfferProductImage';
import ModalConfirm from 'components/layout/modals/ModalConfirm';
import PrimaryButton from 'components/layout/buttons/PrimaryButton';
import SelectInputTranslated from 'components/layout/forms/SelectInputTranslated';
import translations from 'services/translations/translations';
import {
  CONTENT_WIDTH_MM,
  DEFAULT_IMAGE_HEIGHT,
  IMAGE_HEIGHTS,
  getImageHeightMm,
} from 'services/enums/offers/imageHeights';
import { finishLoading, startLoading } from 'store/loading/actions';
import { addMessage } from 'store/messages/actions';

import 'style/offers/OfferProductImages.css';

const ACCEPTED_TYPES = ['image/jpeg', 'image/png'];
const heightOptions = IMAGE_HEIGHTS.map((option) => option.value);

// Shows the image inside a frame that has the same proportions as the image area in the
// offer PDF, so the user sees how the image will look on the offer.
const ImageFrame = ({
  height,
  src,
}: {
  height: string;
  src: string | undefined;
}): JSX.Element => (
  <div className="offer-product-image-frame">
    <div
      className="offer-product-image-frame-ratio"
      style={{
        paddingTop: `${(getImageHeightMm(height) / CONTENT_WIDTH_MM) * 100}%`,
      }}
    >
      {src === undefined ? null : <img src={src} alt="" />}
    </div>
  </div>
);

const OfferProductImages = ({
  locked,
  offerProductId,
}: {
  locked: boolean;
  offerProductId: number;
}): JSX.Element => {
  const dispatch = useDispatch();
  const { t } = useTranslation([translations.offers, translations.common]);
  const fileInput = useRef<HTMLInputElement>(null);

  const [images, setImages] = useState<IOfferProductImage[]>([]);
  const [imageUrls, setImageUrls] = useState<{ [imageId: number]: string }>({});
  const [newImageHeight, setNewImageHeight] = useState(DEFAULT_IMAGE_HEIGHT);
  const [imageToDelete, setImageToDelete] = useState<IOfferProductImage>();
  const [dragActive, setDragActive] = useState(false);

  const loadImages = async () => {
    try {
      setImages(await apiOffers.products.images.get(offerProductId));
    } catch (error) {
      setImages([]);
    }
  };

  useEffect(() => {
    loadImages();
  }, [offerProductId]);

  // The image files require authorization, so they are fetched as blobs
  useEffect(() => {
    let cancelled = false;
    const missingImages = images.filter((image) => !imageUrls[image.id]);

    if (missingImages.length === 0) {
      return undefined;
    }

    const fetchImages = async () => {
      const fetched: { [imageId: number]: string } = {};

      await Promise.all(
        missingImages.map(async (image) => {
          try {
            const response = await apiOffers.products.images.getFile(
              offerProductId,
              image.id
            );
            const blob = new Blob([response.data], {
              type: response.headers['content-type'],
            });
            fetched[image.id] = URL.createObjectURL(blob);
          } catch (error) {
            // The frame is shown empty if the image cannot be loaded
          }
        })
      );

      if (!cancelled) {
        setImageUrls((current) => ({ ...current, ...fetched }));
      }
    };

    fetchImages();

    return () => {
      cancelled = true;
    };
  }, [images]);

  useEffect(
    () => () => {
      Object.values(imageUrls).forEach((url) => URL.revokeObjectURL(url));
    },
    []
  );

  // Adds the chosen or dropped images in order with the selected height
  const addImages = async (files: File[]) => {
    if (files.length === 0) {
      return;
    }

    const imageFiles = files.filter((file) =>
      ACCEPTED_TYPES.includes(file.type)
    );

    if (imageFiles.length < files.length) {
      dispatch(addMessage(t(`${translations.offers}:Image type not allowed`)));
    }

    if (imageFiles.length === 0) {
      return;
    }

    dispatch(startLoading('offer-product-image'));

    try {
      // eslint-disable-next-line no-restricted-syntax
      for (const file of imageFiles) {
        // eslint-disable-next-line no-await-in-loop
        const uploaded = await apiFiles.post(file, null);
        // eslint-disable-next-line no-await-in-loop
        await apiOffers.products.images.post(
          offerProductId,
          `${uploaded.id}`,
          newImageHeight
        );
      }

      dispatch(addMessage(t(`${translations.offers}:Image added`)));
    } catch (error) {
      dispatch(addMessage(t(`${translations.offers}:Image failed`)));
    } finally {
      await loadImages();
      dispatch(finishLoading('offer-product-image'));
    }
  };

  const addSelectedImages = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? []);
    // eslint-disable-next-line no-param-reassign
    event.target.value = '';
    addImages(files);
  };

  const isFileDrag = (event: React.DragEvent) =>
    Array.from(event.dataTransfer.types).includes('Files');

  const onDragOver = (event: React.DragEvent) => {
    if (!isFileDrag(event)) {
      return;
    }

    event.preventDefault();
    event.dataTransfer.dropEffect = 'copy';
    setDragActive(true);
  };

  const onDragLeave = (event: React.DragEvent) => {
    // Leaving to a child element of the drop area is not leaving the area
    if (!event.currentTarget.contains(event.relatedTarget as Node)) {
      setDragActive(false);
    }
  };

  const onDrop = (event: React.DragEvent) => {
    event.preventDefault();
    setDragActive(false);
    addImages(Array.from(event.dataTransfer.files));
  };

  const changeHeight = async (image: IOfferProductImage, height: string) => {
    try {
      await apiOffers.products.images.patch(offerProductId, image.id, height);
      setImages(
        images.map((existing) =>
          existing.id === image.id ? { ...existing, height } : existing
        )
      );
    } catch (error) {
      dispatch(addMessage(t(`${translations.offers}:Image failed`)));
    }
  };

  const deleteImage = async () => {
    const image = imageToDelete;
    setImageToDelete(undefined);

    if (image === undefined) {
      return;
    }

    try {
      await apiOffers.products.images.delete(offerProductId, image.id);
      setImages(images.filter((existing) => existing.id !== image.id));
    } catch (error) {
      dispatch(addMessage(t(`${translations.offers}:Image failed`)));
    }
  };

  return (
    <div className="offer-product-images">
      <ModalConfirm
        showing={imageToDelete !== undefined}
        setShowing={() => setImageToDelete(undefined)}
        header={t(`${translations.offers}:Delete image`)}
        text={t(`${translations.offers}:Delete image confirmation`)}
        yesButton={t(`${translations.common}:Confirm`)}
        noButton={t(`${translations.common}:Cancel`)}
        confirm={deleteImage}
      />
      <Row>
        <Col>
          <h3>{t(`${translations.offers}:Images`).toUpperCase()}</h3>
          <p className="offer-product-images-info">
            {t(`${translations.offers}:Images info`)}
          </p>
        </Col>
      </Row>
      {images.map((image) => (
        <Row key={image.id} className="offer-product-image">
          <Col xs={8}>
            <ImageFrame height={image.height} src={imageUrls[image.id]} />
          </Col>
          <Col xs={4}>
            <p className="offer-product-image-name">{image.name}</p>
            <SelectInputTranslated
              disabled={locked}
              key={`image-height-${image.id}`}
              label={t(`${translations.offers}:Image height`)}
              name={`image-height-${image.id}`}
              onChange={(event: any) => changeHeight(image, event.target.value)}
              options={heightOptions}
              translationPath={`${translations.offers}:imageHeights`}
              value={image.height}
            />
            {locked ? null : (
              <PrimaryButton onClick={() => setImageToDelete(image)}>
                {t(`${translations.common}:Delete`).toLocaleUpperCase()}
              </PrimaryButton>
            )}
          </Col>
        </Row>
      ))}
      {locked ? null : (
        <Row className="offer-product-image">
          <Col xs={8}>
            <div
              className={`offer-product-image-drop-zone${
                dragActive ? ' drag-active' : ''
              }`}
              onClick={() => fileInput.current?.click()}
              onDragEnter={onDragOver}
              onDragLeave={onDragLeave}
              onDragOver={onDragOver}
              onDrop={onDrop}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  fileInput.current?.click();
                }
              }}
              role="button"
              tabIndex={0}
            >
              <ImageFrame height={newImageHeight} src={undefined} />
              <span className="offer-product-image-drop-text">
                {t(`${translations.offers}:Drop images here`)}
              </span>
            </div>
          </Col>
          <Col xs={4}>
            <SelectInputTranslated
              key="new-image-height"
              label={t(`${translations.offers}:Image height`)}
              name="new-image-height"
              onChange={(event: any) => setNewImageHeight(event.target.value)}
              options={heightOptions}
              translationPath={`${translations.offers}:imageHeights`}
              value={newImageHeight}
            />
            <input
              accept={ACCEPTED_TYPES.join(',')}
              hidden
              multiple
              onChange={addSelectedImages}
              ref={fileInput}
              type="file"
            />
            <PrimaryButton onClick={() => fileInput.current?.click()}>
              {t(`${translations.offers}:Add image`).toLocaleUpperCase()}
            </PrimaryButton>
          </Col>
        </Row>
      )}
    </div>
  );
};

export default OfferProductImages;
